import { useState } from "react";
import clsx from "clsx";
import Card from "@albertsons/uds/molecule/Card";
import SkeletonLoader from "@albertsons/uds/molecule/SkeletonLoader";
import MenuButton from "@albertsons/uds/atom/component/MenuButton/MenuButton";
import useWindowBreakpoints from "../../../hooks/useWindowBreakpoints";
import { TabHeader } from "../../../components";
import { formatDateTime } from "../../../utils/date-time-format";
import { ICard } from "../../../types/cards.types";
import { UnitTab } from "../../../types/data.types";
import { transformQuantity } from "../../../utils/formatNumber";
import "./styles.css";

interface CardsProps<T> {
  forceScreen?: "extraSmall" | "mobile" | "desktop";
  sectionLabel: string;
  cards: ICard<T>[];
  isLoading: boolean;
  researchData: T | null;
  showCollapsedView?: boolean;
  selectedUnitTab?: UnitTab;
}

export const Cards = <T,>(props: CardsProps<T>) => {
  const {
    forceScreen,
    sectionLabel,
    cards,
    isLoading,
    researchData,
    showCollapsedView = false,
    selectedUnitTab,
  } = props;

  const [isExpandedCards, setIsExpandedCards] = useState(false);

  const { getScreen } = useWindowBreakpoints();
  const screen = getScreen(forceScreen);

  const showAppliedForNotReceiveInvoiceTag = cards.some(
    (card) => card.forNotReceivedInvoice,
  );

  const getCardValue = (card: ICard<T>) => {
    let key = card.key;

    // Handle thisWeeksDemand based on selectedUnitTab
    if (
      key === "thisWeeksDemand" &&
      selectedUnitTab !== undefined &&
      researchData
    ) {
      const unitTabKeys: Record<UnitTab, keyof T> = {
        0: "thisWeeksDemandSellingUnits" as keyof T,
        1: "thisWeeksDemandShippingUnits" as keyof T,
        2: "thisWeeksDemandVendorUnits" as keyof T,
      };
      key = unitTabKeys[selectedUnitTab] || key;
    }

    const value = researchData?.[key];
    if (value !== undefined && value !== null && value !== "") {
      if (card.type === "date") {
        return formatDateTime(value as string | number).date;
      }
      if (card.type === "number") {
        return transformQuantity(value as string | number);
      }
      return `${value as string | number}`;
    }
    return "NA";
  };

  const onClickCards = (): void => {
    setIsExpandedCards((prev) => !prev);
    if (!isExpandedCards) {
      setTimeout(() => {
        const element = document.getElementById("cards-wrapper");
        element?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  return (
    <div className="mt-3" id="cards-wrapper">
      {showCollapsedView ? (
        <MenuButton
          className="w-full border border-[#b4b4b4] text-sm rounded-2xl"
          id="cards-button"
          open={isExpandedCards}
          onClick={onClickCards}
        >
          <div className="flex items-center justify-between w-[calc(100%_-_24px)]">
            <span className="font-medium truncate text-[#0273df]">
              {sectionLabel}
            </span>
          </div>
        </MenuButton>
      ) : (
        <TabHeader title={sectionLabel} />
      )}
      {(isExpandedCards || !showCollapsedView) && (
        <>
          <div
            className={clsx("pt-5 pb-2", screen.extraSmall ? "px-2" : "px-4")}
          >
            <div className="flex flex-col">
              <div
                className={clsx(
                  "grid",
                  screen.isMobile ? "grid-cols-3" : "grid-cols-6",
                  "place-items-center gap-4",
                )}
              >
                {cards.map((card) => (
                  <Card
                    className={clsx(
                      "bg-white border-none rounded-none shadow-xl dc-research-cards w-full",
                      screen.extraSmall ? "h-[96px]" : "h-[76px]",
                    )}
                    key={card.key as string}
                  >
                    <Card.Header>
                      <div className="bg-neutral-300 border-b-4 w-full border-sky-700 h-full">
                        <div className="font-semibold flex items-center justify-center text-center text-xs">
                          <p
                            className={clsx(
                              "mt-1",
                              screen.extraSmall && "text-[11px]",
                            )}
                          >
                            {card.label}
                            {!card.label2 && card.forNotReceivedInvoice ? (
                              <span className="text-red-500">*</span>
                            ) : (
                              ""
                            )}
                            <br />
                            {card.label2 || (
                              <span className="invisible">PlaceholderText</span>
                            )}
                            {card.label2 && card.forNotReceivedInvoice ? (
                              <span className="text-red-500">*</span>
                            ) : (
                              ""
                            )}
                          </p>
                        </div>
                      </div>
                    </Card.Header>
                    <Card.Content>
                      <div className="items-center font-semibold justify-center text-center !p-2">
                        {isLoading &&
                        (!researchData || !researchData[card.key]) ? (
                          <SkeletonLoader skeletonTheme baseColor="#dadce2" />
                        ) : (
                          <p
                            className="text-sm truncate"
                            title={getCardValue(card)}
                          >
                            {getCardValue(card)}
                          </p>
                        )}
                      </div>
                    </Card.Content>
                  </Card>
                ))}
              </div>
            </div>
          </div>
          {showAppliedForNotReceiveInvoiceTag ? (
            <p className="text-sm pt-2">
              <span className="text-red-500">*</span>
              {"applies to outstanding order (not received)"}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
};

export default Cards;
